import { useCallback, useEffect, useReducer, useRef } from 'react';

import {
    GenerationsOccurrenceList,
    type GenerationsRecurrenceSelectionPreview,
    loadGenerationsRecurrenceSelection,
} from '@apps/web/components/outreaches/generations_recurrence_preview';
import {
    Alert,
    AlertDescription,
    AlertTitle,
} from '@apps/web/components/ui/alert';
import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import { Spinner } from '@apps/web/components/ui/spinner';
import { getApiClient } from '@apps/web/lib/api_client';
import { ApiError } from '@libraries/typescript/api_client';

/**
 * One shift's unambiguous caregiver-specific Generations payroll option.
 * `previewDigest` binds the item and rate to the vendor state the preview
 * read; the fill request echoes it back so the writer can refuse a stale
 * approval. The rate is kept as the exact string Generations rendered
 * (four decimals) and is never reformatted or edited.
 */
export type GenerationsPayrollPreview = {
    readonly payrollItemName: string;
    readonly payrollRate: string;
    readonly previewDigest: string;
};

const GENERATIONS_PREVIEW_FALLBACK_MESSAGE =
    'Generations could not confirm a pay rate.';
const GENERATIONS_ASSIGNMENT_CONFLICT_CODE =
    'generations_recurrence_conflicting_assignment';

export const GENERATIONS_PREVIEW_GUIDANCE_MESSAGE =
    'Check the visit in Generations. Close and reopen to try writeback again, or continue with a Phoebe-only assignment.';

/**
 * Prefer the server's safe 409 message: every structured refusal from the
 * preview route is a settled state with copy written for coordinators. Any
 * other failure (network, 5xx, lost outcome) gets a plain refusal rather
 * than a guess about what happened upstream.
 */
type GenerationsPreviewError = {
    readonly code: string | null;
    readonly message: string;
};

function getGenerationsPreviewError(error: unknown): GenerationsPreviewError {
    if (error instanceof ApiError && error.status === 409) {
        const body: unknown = error.body;
        if (
            body !== null &&
            typeof body === 'object' &&
            'detail' in body &&
            body.detail !== null &&
            typeof body.detail === 'object' &&
            'message' in body.detail &&
            typeof body.detail.message === 'string' &&
            body.detail.message.length > 0
        ) {
            return {
                code:
                    'code' in body.detail &&
                    typeof body.detail.code === 'string'
                        ? body.detail.code
                        : null,
                message: body.detail.message,
            };
        }
    }
    return { code: null, message: GENERATIONS_PREVIEW_FALLBACK_MESSAGE };
}

type UseGenerationsPayrollPreviewsArgs = {
    // False outside an open live-mode Generations confirmation; flipping it
    // (or changing any identity below) discards previews and confirmation.
    readonly enabled: boolean;
    readonly organizationId: string | null;
    readonly outreachId: string;
    readonly caregiverId: string;
    // Canonical key of the selected shift set. The human confirmation
    // approves one exact set, so any change to the key voids it.
    readonly selectionKey: string;
};

type UseGenerationsPayrollPreviewsResult = {
    readonly previewsByShiftId: ReadonlyMap<string, GenerationsPayrollPreview>;
    readonly isLoading: boolean;
    readonly loadError: string | null;
    readonly loadErrorCode: string | null;
    readonly isConfirmed: boolean;
    readonly recurrenceSelection: GenerationsRecurrenceSelectionPreview | null;
    readonly idempotentShiftIds: ReadonlySet<string>;
    readonly setConfirmed: (confirmed: boolean) => void;
    readonly loadPreviews: (shiftIds: ReadonlyArray<string>) => Promise<void>;
};

type GenerationsPreviewData = {
    readonly previewsByShiftId: ReadonlyMap<string, GenerationsPayrollPreview>;
    readonly recurrenceSelection: GenerationsRecurrenceSelectionPreview | null;
    readonly idempotentShiftIds: ReadonlySet<string>;
    readonly isConfirmed: boolean;
};

type GenerationsPreviewState =
    | {
          readonly phase: 'idle';
          readonly data: GenerationsPreviewData;
      }
    | {
          readonly phase: 'loading';
          readonly data: GenerationsPreviewData;
      }
    | {
          readonly phase: 'mismatch';
          readonly data: GenerationsPreviewData;
      }
    | {
          readonly phase: 'ready';
          readonly data: GenerationsPreviewData;
      }
    | {
          readonly phase: 'error';
          readonly data: GenerationsPreviewData;
          readonly error: GenerationsPreviewError;
      };

type GenerationsPreviewAction =
    | { readonly type: 'invalidate'; readonly requestIsDraining: boolean }
    | { readonly type: 'start' }
    | {
          readonly type: 'recurrenceLoaded';
          readonly selection: GenerationsRecurrenceSelectionPreview;
      }
    | {
          readonly type: 'payrollLoaded';
          readonly shiftId: string;
          readonly preview: GenerationsPayrollPreview;
      }
    | { readonly type: 'mismatch' }
    | { readonly type: 'complete'; readonly confirmed: boolean }
    | { readonly type: 'error'; readonly error: GenerationsPreviewError }
    | { readonly type: 'confirm'; readonly confirmed: boolean }
    | { readonly type: 'drained' };

function assertNever(value: never): never {
    throw new Error(`Unhandled Generations preview action: ${String(value)}`);
}

const EMPTY_PREVIEW_DATA: GenerationsPreviewData = {
    previewsByShiftId: new Map(),
    recurrenceSelection: null,
    idempotentShiftIds: new Set(),
    isConfirmed: false,
};

function generationsPreviewReducer(
    state: GenerationsPreviewState,
    action: GenerationsPreviewAction,
): GenerationsPreviewState {
    switch (action.type) {
        case 'invalidate':
            return {
                phase: action.requestIsDraining ? 'loading' : 'idle',
                data: EMPTY_PREVIEW_DATA,
            };
        case 'start':
            return { phase: 'loading', data: state.data };
        case 'recurrenceLoaded':
            return {
                phase: 'loading',
                data: {
                    ...state.data,
                    recurrenceSelection: action.selection,
                    idempotentShiftIds: new Set(
                        action.selection.idempotent_shift_ids,
                    ),
                },
            };
        case 'payrollLoaded':
            return {
                phase: 'loading',
                data: {
                    ...state.data,
                    previewsByShiftId: new Map(
                        state.data.previewsByShiftId,
                    ).set(action.shiftId, action.preview),
                },
            };
        case 'mismatch':
            return { phase: 'mismatch', data: state.data };
        case 'complete':
            return {
                phase: 'ready',
                data: { ...state.data, isConfirmed: action.confirmed },
            };
        case 'error':
            return { phase: 'error', data: state.data, error: action.error };
        case 'confirm':
            return state.phase === 'ready'
                ? {
                      phase: 'ready',
                      data: { ...state.data, isConfirmed: action.confirmed },
                  }
                : state;
        case 'drained':
            return { phase: 'idle', data: EMPTY_PREVIEW_DATA };
        default:
            return assertNever(action);
    }
}

type ActivePreviewRun = {
    readonly isValid: () => boolean;
    readonly invalidate: () => void;
};

function createActivePreviewRun(): ActivePreviewRun {
    let invalidated = false;
    return {
        isValid: () => !invalidated,
        invalidate: () => {
            invalidated = true;
        },
    };
}

/**
 * Per-shift Generations payroll previews for the Assign & Fill confirmation.
 *
 * The preview route performs a real (certified non-persisting) vendor
 * postback, so requests happen only from an explicit load action. Payroll
 * requests stay serial so the first refusal stops before any later vendor
 * postback starts.
 */
export function useGenerationsPayrollPreviews({
    enabled,
    organizationId,
    outreachId,
    caregiverId,
    selectionKey,
}: UseGenerationsPayrollPreviewsArgs): UseGenerationsPayrollPreviewsResult {
    const [state, dispatch] = useReducer(generationsPreviewReducer, {
        phase: 'idle',
        data: EMPTY_PREVIEW_DATA,
    });
    // One active token synchronously prevents overlapping vendor sessions and
    // lets an invalidated serial batch stop after its current request drains.
    const activeRunRef = useRef<ActivePreviewRun | null>(null);

    useEffect(() => {
        const activeRun = activeRunRef.current;
        if (activeRun) {
            activeRun.invalidate();
        }
        dispatch({
            type: 'invalidate',
            requestIsDraining: activeRun !== null,
        });
    }, [enabled, organizationId, outreachId, caregiverId, selectionKey]);

    const loadPreviews = useCallback(
        async (shiftIds: ReadonlyArray<string>) => {
            if (
                !enabled ||
                !organizationId ||
                activeRunRef.current !== null ||
                state.phase !== 'idle'
            ) {
                return;
            }
            const run = createActivePreviewRun();
            activeRunRef.current = run;
            dispatch({ type: 'start' });

            const isCurrent = () =>
                activeRunRef.current === run && run.isValid();
            try {
                let payrollShiftIds = shiftIds;
                if (shiftIds.length > 1) {
                    let selection: GenerationsRecurrenceSelectionPreview;
                    try {
                        selection = await loadGenerationsRecurrenceSelection({
                            organizationId,
                            outreachId,
                            caregiverId,
                            shiftIds,
                        });
                    } catch (error) {
                        if (isCurrent()) {
                            dispatch({
                                type: 'error',
                                error: getGenerationsPreviewError(error),
                            });
                        }
                        return;
                    }
                    if (!isCurrent()) {
                        return;
                    }
                    dispatch({ type: 'recurrenceLoaded', selection });
                    payrollShiftIds = selection.assignment_shift_ids;
                    if (payrollShiftIds.length === 0) {
                        dispatch({ type: 'complete', confirmed: true });
                        return;
                    }
                    if (
                        selection.recurrence_applies &&
                        !selection.selection_matches
                    ) {
                        dispatch({ type: 'mismatch' });
                        return;
                    }
                }

                for (const shiftId of payrollShiftIds) {
                    let preview: GenerationsPayrollPreview;
                    try {
                        const response = await getApiClient().post({
                            url: '/organizations/{organization_id}/callouts/{callout_id}/generations-payroll-selection',
                            pathParams: {
                                organization_id: organizationId,
                                callout_id: outreachId,
                            },
                            queryParams: {
                                caregiver_id: caregiverId,
                                shift_id: shiftId,
                            },
                            reason: 'GenerationsPayrollPreview',
                        });
                        preview = {
                            payrollItemName: response.payroll_item_name,
                            payrollRate: response.payroll_rate,
                            previewDigest: response.preview_digest,
                        };
                    } catch (error) {
                        if (isCurrent()) {
                            dispatch({
                                type: 'error',
                                error: getGenerationsPreviewError(error),
                            });
                        }
                        return;
                    }
                    if (!isCurrent()) {
                        return;
                    }
                    dispatch({ type: 'payrollLoaded', shiftId, preview });
                }
                dispatch({ type: 'complete', confirmed: false });
            } finally {
                if (activeRunRef.current === run) {
                    activeRunRef.current = null;
                    if (!run.isValid()) {
                        dispatch({ type: 'drained' });
                    }
                }
            }
        },
        [enabled, organizationId, outreachId, caregiverId, state.phase],
    );

    return {
        previewsByShiftId: state.data.previewsByShiftId,
        isLoading: state.phase === 'loading',
        loadError: state.phase === 'error' ? state.error.message : null,
        loadErrorCode: state.phase === 'error' ? state.error.code : null,
        isConfirmed: state.data.isConfirmed,
        recurrenceSelection: state.data.recurrenceSelection,
        idempotentShiftIds: state.data.idempotentShiftIds,
        setConfirmed: (confirmed) => dispatch({ type: 'confirm', confirmed }),
        loadPreviews,
    };
}

type GenerationsShiftPayrollLineProps = {
    readonly preview: GenerationsPayrollPreview | null;
    readonly isLoading: boolean;
    readonly isIdempotent?: boolean;
};

/**
 * Read-only per-shift payroll confirmation line. This is deliberately not a
 * picker: the server returns only a vendor-selected option or the sole active
 * option in the caregiver-specific picker. Multiple active options refuse.
 */
export function GenerationsShiftPayrollLine({
    preview,
    isLoading,
    isIdempotent = false,
}: GenerationsShiftPayrollLineProps) {
    if (isIdempotent) {
        return (
            <p className="text-muted-foreground mt-2 pl-8 text-xs">
                Already assigned to this caregiver; no Generations write needed
            </p>
        );
    }
    if (preview) {
        return (
            <p className="text-foreground mt-2 pl-8 text-xs">
                Generations payroll: {preview.payrollItemName} — $
                {preview.payrollRate}
            </p>
        );
    }
    return (
        <p className="text-muted-foreground mt-2 pl-8 text-xs">
            {isLoading
                ? 'Loading payroll from Generations…'
                : 'Payroll not loaded yet'}
        </p>
    );
}

type GenerationsPayrollSectionProps = {
    readonly selectedShiftCount: number;
    readonly assignmentShiftCount: number;
    readonly loadedShiftCount: number;
    readonly allSelectedShiftsLoaded: boolean;
    readonly isLoading: boolean;
    readonly disabled: boolean;
    readonly loadError: string | null;
    readonly loadErrorCode: string | null;
    readonly isConfirmed: boolean;
    readonly recurrenceSelection: GenerationsRecurrenceSelectionPreview | null;
    readonly onLoad: () => void;
    readonly onConfirmedChange: (confirmed: boolean) => void;
    readonly onContinueWithoutWriteback: () => void;
};

/**
 * The explicit load action, batch progress, settled failures, and the
 * deliberate human confirmation for Generations payroll. A failed preview is
 * not automatically retried for the same preview context. The coordinator can
 * inspect Generations and reopen, or explicitly continue with a Phoebe-only
 * assignment.
 */
export function GenerationsPayrollSection({
    selectedShiftCount,
    assignmentShiftCount,
    loadedShiftCount,
    allSelectedShiftsLoaded,
    isLoading,
    disabled,
    loadError,
    loadErrorCode,
    isConfirmed,
    recurrenceSelection,
    onLoad,
    onConfirmedChange,
    onContinueWithoutWriteback,
}: GenerationsPayrollSectionProps) {
    return (
        <div className="border-border bg-background mt-3 space-y-3 rounded-lg border p-4">
            <div className="space-y-1">
                <p className="font-medium">Generations payroll</p>
                <p className="text-muted-foreground text-sm">
                    Load the unambiguous payroll item and pay rate Generations
                    shows for this caregiver, then confirm them to assign.
                    Loading does not change anything in Generations.
                </p>
                {selectedShiftCount > 1 && (
                    <p className="text-muted-foreground text-sm">
                        Phoebe will first check which selected visits still need
                        a Generations write. When more than one does, it will
                        verify the complete finite date span and will not change
                        all future visits.
                    </p>
                )}
            </div>
            {recurrenceSelection && !recurrenceSelection.selection_matches ? (
                <Alert variant="destructive">
                    <AlertTitle>
                        Generations would use a different set of visits
                    </AlertTitle>
                    <AlertDescription>
                        <p>
                            Nothing changed. Adjust the selection or assign
                            these visits individually.
                        </p>
                        {recurrenceSelection.extra_computed_occurrences.length >
                            0 && (
                            <div>
                                <p className="font-medium">Also included</p>
                                <GenerationsOccurrenceList
                                    occurrences={
                                        recurrenceSelection.extra_computed_occurrences
                                    }
                                />
                            </div>
                        )}
                        {recurrenceSelection.missing_selected_occurrences
                            .length > 0 && (
                            <div>
                                <p className="font-medium">Not included</p>
                                <GenerationsOccurrenceList
                                    occurrences={
                                        recurrenceSelection.missing_selected_occurrences
                                    }
                                />
                            </div>
                        )}
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onContinueWithoutWriteback}
                            disabled={disabled}
                        >
                            Continue without Generations writeback
                        </Button>
                    </AlertDescription>
                </Alert>
            ) : loadError ? (
                <div
                    className="space-y-1 rounded border border-red-200 bg-red-50 p-3"
                    role="alert"
                >
                    <p className="text-sm font-medium text-red-800">
                        {loadError}
                    </p>
                    {loadErrorCode === GENERATIONS_ASSIGNMENT_CONFLICT_CODE ? (
                        <p className="text-sm text-red-700">
                            Change the selected visits or caregiver before
                            assigning.
                        </p>
                    ) : (
                        <div className="space-y-2">
                            <p className="text-sm text-red-700">
                                {GENERATIONS_PREVIEW_GUIDANCE_MESSAGE}
                            </p>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={onContinueWithoutWriteback}
                                disabled={disabled}
                            >
                                Continue without Generations writeback
                            </Button>
                        </div>
                    )}
                </div>
            ) : allSelectedShiftsLoaded ? (
                <div className="space-y-3">
                    {recurrenceSelection?.recurrence_applies &&
                        recurrenceSelection.selection_matches && (
                            <div className="space-y-1 rounded border p-3">
                                <p className="text-sm font-medium">
                                    Generations occurrences (
                                    {
                                        recurrenceSelection.computed_occurrences
                                            .length
                                    }
                                    )
                                </p>
                                <GenerationsOccurrenceList
                                    occurrences={
                                        recurrenceSelection.computed_occurrences
                                    }
                                />
                            </div>
                        )}
                    {assignmentShiftCount === 0 ? (
                        <p className="text-muted-foreground text-sm">
                            All selected visits are already assigned to this
                            caregiver. No Generations write is needed.
                        </p>
                    ) : (
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="confirmGenerationsPayroll"
                                checked={isConfirmed}
                                onCheckedChange={(checked) =>
                                    onConfirmedChange(checked === true)
                                }
                                disabled={disabled}
                            />
                            <label
                                htmlFor="confirmGenerationsPayroll"
                                className="cursor-pointer text-sm font-medium"
                            >
                                I confirm the Generations{' '}
                                {recurrenceSelection?.recurrence_applies
                                    ? 'occurrences, payroll items, and rates'
                                    : 'payroll item and rate'}{' '}
                                shown above
                            </label>
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex items-center gap-3">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onLoad}
                        disabled={
                            disabled || isLoading || selectedShiftCount === 0
                        }
                    >
                        Load payroll from Generations
                    </Button>
                    {isLoading ? (
                        <p
                            role="status"
                            className="text-muted-foreground flex items-center gap-2 text-sm"
                        >
                            <Spinner size="md" />
                            Loading from Generations ({loadedShiftCount}/
                            {selectedShiftCount})…
                        </p>
                    ) : null}
                </div>
            )}
        </div>
    );
}
